import { useState } from 'react';

const ENTRA_AUTHORITY = 'https://login.microsoftonline.com/417ca393-15d2-4e55-beec-87affb35daa4/v2.0';
const ENTRA_CLIENT_ID = 'd2d6ab2d-e89c-49d6-810f-426cc7c0234c';
const FORGEROCK_AUTHORITY = 'https://ep.fram.qa.idm.toyota.com/openam/oauth2/realms/root/realms/dealerdaily';
const FORGEROCK_CLIENT_ID = 'DSMETACT';
const ENTRA_SESSION_KEY = `oidc.user:${ENTRA_AUTHORITY}:${ENTRA_CLIENT_ID}`;
const FORGEROCK_SESSION_KEY = `oidc.user:${FORGEROCK_AUTHORITY}:${FORGEROCK_CLIENT_ID}`;

const isAuthSelectorHost = (hostname) =>
  hostname === 'localhost' ||
  hostname === '127.0.0.1' ||
  hostname === 'cubedev.toyota.com' ||
  hostname.endsWith('.cubedev.toyota.com');

// Returns the session's expiry (Infinity when absent) or null if there is no active session.
const getActiveOidcSessionExpiry = (storage, sessionKey, now) => {
  const storedUser = storage.getItem(sessionKey);

  if (!storedUser) {
    return null;
  }

  try {
    const user = JSON.parse(storedUser);
    const expiresAt = user.expires_at;
    const hasSubject = Boolean(user.profile?.sub);
    const hasToken = Boolean(user.access_token);
    const isActive = hasToken && hasSubject && (expiresAt === undefined || expiresAt > Math.floor(now / 1000));
    return isActive ? expiresAt ?? Infinity : null;
  } catch {
    return null;
  }
};

export const clearOtherAuthenticatorSession = (authenticator, sessionStorage) => {
  sessionStorage.removeItem(authenticator === 'entra' ? FORGEROCK_SESSION_KEY : ENTRA_SESSION_KEY);
};

const getAuthenticatorFromSignInState = (stateId, stateStorage) => {
  if (!stateId) {
    return null;
  }

  const storedState = stateStorage.getItem(`oidc.${stateId}`);

  if (!storedState) {
    return null;
  }

  try {
    const signInState = JSON.parse(storedState);
    if (signInState.authority === ENTRA_AUTHORITY && signInState.client_id === ENTRA_CLIENT_ID) {
      return 'entra';
    }
    if (signInState.authority === FORGEROCK_AUTHORITY && signInState.client_id === FORGEROCK_CLIENT_ID) {
      return 'forgerock';
    }
  } catch {
    return null;
  }

  return null;
};

export const getInitialAuthenticator = (search, hostname, sessionStorage, stateStorage, now = Date.now()) => {
  if (!isAuthSelectorHost(hostname)) {
    return 'forgerock';
  }

  const searchParams = new URLSearchParams(search);
  const isOidcCallback = searchParams.has('code') || searchParams.has('state') || searchParams.has('error');

  if (isOidcCallback) {
    return getAuthenticatorFromSignInState(searchParams.get('state'), stateStorage) ?? 'forgerock';
  }

  const entraExpiry = getActiveOidcSessionExpiry(sessionStorage, ENTRA_SESSION_KEY, now);
  const forgeRockExpiry = getActiveOidcSessionExpiry(sessionStorage, FORGEROCK_SESSION_KEY, now);

  if (entraExpiry === null && forgeRockExpiry === null) {
    return null;
  }

  // With both sessions present, the one expiring last is the most recent login; ForgeRock wins ties.
  return entraExpiry !== null && (forgeRockExpiry === null || entraExpiry > forgeRockExpiry) ? 'entra' : 'forgerock';
};

export const useAuthenticatorSelector = () => {
  const [selectedAuthenticator, setSelectedAuthenticator] = useState(() => {
    const authenticator = getInitialAuthenticator(
      globalThis.location.search,
      globalThis.location.hostname,
      globalThis.sessionStorage,
      globalThis.localStorage,
    );

    if (authenticator) {
      clearOtherAuthenticatorSession(authenticator, globalThis.sessionStorage);
    }

    return authenticator;
  });

  const selectAuthenticator = (selectedAuthenticator) => {
    clearOtherAuthenticatorSession(selectedAuthenticator, globalThis.sessionStorage);
    setSelectedAuthenticator(selectedAuthenticator);
  };

  return { selectedAuthenticator, selectAuthenticator };
};
